import React, { useState, useEffect, useMemo } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";
import { S, PRIORITY_COLOR, todayStr } from "../../crm/theme";
import { fetchLeads, fetchTasks, createTask, updateTask, deleteTask, visibleTo } from "../../crm/crmApi";
import TaskModal from "../../crm/TaskModal";
import { fmtD } from "../../crm/dateFormat";

export default function CrmTasksScreen({ user }) {
  const [tasks, setTasks] = useState([]);
  const [leads, setLeads] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [modalTask, setModalTask] = useState(undefined);
  const [showDone, setShowDone] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [allLeads, allTasks, usersSnap] = await Promise.all([
        fetchLeads(), fetchTasks(), getDocs(collection(db, "platformUsers")),
      ]);
      setLeads(visibleTo(allLeads, user));
      setTasks(visibleTo(allTasks, user));
      setSalesUsers(
        usersSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((u) => u.role === "Sales" || u.role === "SalesHead")
      );
    } catch (e) {
      setMsg("Error loading tasks: " + e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [user]);

  const leadName = (leadId) => leads.find((l) => l.id === leadId)?.societyName || null;
  const today = todayStr();

  const visible = useMemo(
    () => tasks.filter((t) => (showDone ? true : t.status !== "done")).sort((a, b) => (a.dueDate || "").localeCompare(b.dueDate || "")),
    [tasks, showDone]
  );

  const toggleDone = async (task) => {
    await updateTask(task.id, { status: task.status === "done" ? "pending" : "done" });
    await load();
  };

  const handleSave = async (data) => {
    if (modalTask && modalTask.id) {
      await updateTask(modalTask.id, data);
      setMsg("Task updated.");
    } else {
      await createTask(data, user);
      setMsg("Task added.");
    }
    await load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    await deleteTask(id);
    setModalTask(undefined);
    setMsg("Task deleted.");
    await load();
  };

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div style={S.title}>✅ Tasks</div>
        <button style={S.btnBlue} onClick={() => setModalTask(null)}>+ New Task</button>
      </div>

      {msg && <div style={S.msg}>{msg}</div>}

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: "#7b82a0", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
          Show completed tasks
        </label>
      </div>

      {loading ? (
        <div style={{ color: "#7b82a0" }}>Loading…</div>
      ) : visible.length === 0 ? (
        <div style={{ color: "#7b82a0" }}>No tasks to show.</div>
      ) : (
        <div style={S.tableWrap}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={S.th}></th><th style={S.th}>Task</th><th style={S.th}>Lead</th>
                <th style={S.th}>Due</th><th style={S.th}>Priority</th><th style={S.th}>Assigned To</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => {
                const overdue = t.status !== "done" && t.dueDate && t.dueDate < today;
                const linkedLead = leadName(t.leadId);
                return (
                  <tr key={t.id} style={{ opacity: t.status === "done" ? 0.5 : 1 }}>
                    <td style={S.td}>
                      <input type="checkbox" checked={t.status === "done"} onChange={() => toggleDone(t)} onClick={(e) => e.stopPropagation()} />
                    </td>
                    <td style={{ ...S.td, cursor: "pointer", textDecoration: t.status === "done" ? "line-through" : "none" }} onClick={() => setModalTask(t)}>
                      {t.title}
                    </td>
                    <td style={S.td}>{linkedLead || "—"}</td>
                    <td style={{ ...S.td, color: overdue ? "#f87171" : "#e8eaf0", fontWeight: overdue ? 700 : 400 }}>
                      {fmtD(t.dueDate)}{overdue ? " (overdue)" : ""}
                    </td>
                    <td style={S.td}><span style={S.badge(PRIORITY_COLOR[t.priority] || "#7b82a0")}>{t.priority}</span></td>
                    <td style={{ ...S.td, color: "#7b82a0" }}>{t.assignedToName || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalTask !== undefined && (
        <TaskModal
          task={modalTask}
          leads={leads}
          user={user}
          salesUsers={salesUsers}
          onSave={handleSave}
          onClose={() => setModalTask(undefined)}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}